import { useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, Heart, Volume2, Share2, MapPin, Scale, Clock } from 'lucide-react';
import api from '@/lib/api';
import { Card, Button, Loader, Badge, ConservationBadge, ScratchFact } from '@/components/ui';
import { AnimalViewer3D } from '@/components/animals';
import { useAchievementStore } from '@/stores/achievementStore';
import { useProgressStore } from '@/stores/progressStore';

interface AnimalData {
  animal: {
    id: number;
    name: string;
    name_latin: string;
    slug: string;
    description: string;
    short_description: string;
    image_url: string;
    model_url: string;
    sound_url: string;
    category_id: number;
    category_name: string;
    category_slug: string;
    size_min: number;
    size_max: number;
    weight_min: number;
    weight_max: number;
    lifespan_min: number;
    lifespan_max: number;
    diet: string;
    conservation_status: string;
    continents: string[];
    habitats: string[];
    facts: string[];
    speed_kmh: number;
  };
  related: {
    id: number;
    name: string;
    slug: string;
    image_url: string;
    short_description: string;
  }[];
}

export function Animal() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addAchievements } = useAchievementStore();
  const { addStars } = useProgressStore();
  // Include lang in queryKey so cache is invalidated on language switch
  const lang = i18n.language?.startsWith('en') ? 'en' : 'ru';

  const dietLabels: Record<string, string> = {
    herbivore: t('animal.diet.herbivore'),
    carnivore: t('animal.diet.carnivore'),
    omnivore: t('animal.diet.omnivore'),
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['animal', slug, lang],
    queryFn: async () => {
      // Handle random animal route
      if (slug === 'random') {
        const { data } = await api.get('/api/animals/random');
        navigate(`/animal/${data.animal.slug}`, { replace: true });
        return null;
      }
      const { data } = await api.get<AnimalData>(`/api/animals/${slug}`);
      return data;
    },
    enabled: !!slug,
  });

  const favoriteMutation = useMutation({
    mutationFn: async (animalId: number) => {
      await api.post(`/api/progress/favorite/${animalId}`);
    },
  });

  // Record animal view for achievements
  const viewRecorded = useRef<number | null>(null);
  const recordViewMutation = useMutation({
    mutationFn: async (animalId: number) => {
      const { data } = await api.post(`/api/progress/view/${animalId}`);
      return data;
    },
    onSuccess: (result) => {
      // Show achievement toasts if any
      if (result?.new_achievements?.length > 0) {
        addAchievements(result.new_achievements);
      }
      // Update star counter with animation
      if (result?.stars_earned > 0) {
        addStars(result.stars_earned);
      }
    },
  });

  // Record view when animal is loaded (only once per animal)
  useEffect(() => {
    if (data?.animal?.id && viewRecorded.current !== data.animal.id) {
      viewRecorded.current = data.animal.id;
      recordViewMutation.mutate(data.animal.id);
    }
  }, [data?.animal?.id]);

  const playSound = (url: string) => {
    const audio = new Audio(url);
    audio.play().catch(() => {
      console.log('Sound playback failed');
    });
  };

  if (isLoading || !data) {
    return (
      <div className="relative z-10 flex items-center justify-center py-20">
        <Loader size="lg" text={t('common.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative z-10 text-center py-12">
        <span className="text-6xl mb-4 block">😿</span>
        <h1 className="text-2xl font-bold mb-2">{t('animal.notFound')}</h1>
        <p className="text-gray-400 mb-6">{t('animal.notFoundHint')}</p>
        <Link to="/">
          <Button>{t('common.returnToHome')}</Button>
        </Link>
      </div>
    );
  }

  const { animal, related } = data;

  return (
    <div className="relative z-10 space-y-8">
      {/* Back button */}
      <Link
        to={`/category/${animal.category_slug}`}
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>{animal.category_name}</span>
      </Link>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 3D Viewer */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <AnimalViewer3D
            modelUrl={animal.model_url}
            imageUrl={animal.image_url}
            animalName={animal.name}
            className="h-80"
          />

          {/* Actions */}
          <div className="flex gap-2 mt-4">
            <Button
              variant="secondary"
              icon={<Heart className="w-4 h-4" />}
              onClick={() => favoriteMutation.mutate(animal.id)}
              loading={favoriteMutation.isPending}
            >
              {t('common.toFavorites')}
            </Button>
            <Button
              variant="ghost"
              icon={<Volume2 className="w-4 h-4" />}
              onClick={() => animal.sound_url && playSound(animal.sound_url)}
              disabled={!animal.sound_url}
              title={animal.sound_url ? t('animal.playSound') : t('animal.noSound')}
            >
              {t('common.sound')}
            </Button>
            <Button variant="ghost" icon={<Share2 className="w-4 h-4" />}>
              {t('common.share')}
            </Button>
          </div>
        </motion.div>

        {/* Info */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-6"
        >
          <div>
            <h1 className="text-4xl font-bold mb-2">{animal.name}</h1>
            <p className="text-xl text-gray-400 italic mb-4">{animal.name_latin}</p>
            <div className="flex flex-wrap gap-2">
              {animal.conservation_status && (
                <ConservationBadge status={animal.conservation_status} />
              )}
              {animal.diet && (
                <Badge variant="info">{dietLabels[animal.diet] || animal.diet}</Badge>
              )}
            </div>
          </div>

          <p className="text-gray-300 leading-relaxed">{animal.description}</p>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {animal.size_max && (
              <Card className="text-center py-3">
                <Scale className="w-5 h-5 mx-auto mb-1 text-cosmic-purple" />
                <p className="text-sm text-gray-400">{t('animal.size')}</p>
                <p className="font-bold">
                  {animal.size_min === animal.size_max
                    ? `${animal.size_max} m`
                    : `${animal.size_min}-${animal.size_max} m`}
                </p>
              </Card>
            )}
            {animal.weight_max && (
              <Card className="text-center py-3">
                <Scale className="w-5 h-5 mx-auto mb-1 text-cosmic-purple" />
                <p className="text-sm text-gray-400">{t('animal.weight')}</p>
                <p className="font-bold">
                  {animal.weight_min === animal.weight_max
                    ? `${animal.weight_max} kg`
                    : `${animal.weight_min}-${animal.weight_max} kg`}
                </p>
              </Card>
            )}
            {animal.lifespan_max && (
              <Card className="text-center py-3">
                <Clock className="w-5 h-5 mx-auto mb-1 text-cosmic-purple" />
                <p className="text-sm text-gray-400">{t('animal.lifespan')}</p>
                <p className="font-bold">
                  {animal.lifespan_min === animal.lifespan_max
                    ? `${animal.lifespan_max}`
                    : `${animal.lifespan_min}-${animal.lifespan_max}`}
                </p>
              </Card>
            )}
            {animal.speed_kmh && (
              <Card className="text-center py-3">
                <MapPin className="w-5 h-5 mx-auto mb-1 text-cosmic-purple" />
                <p className="text-sm text-gray-400">{t('animal.speed')}</p>
                <p className="font-bold">{animal.speed_kmh} km/h</p>
              </Card>
            )}
          </div>
        </motion.div>
      </div>

      {/* Facts - Scratch Cards */}
      {animal.facts && animal.facts.length > 0 && (
        <section>
          <h2 className="text-2xl font-bold mb-2">{t('animal.facts')}</h2>
          <p className="text-gray-400 text-sm mb-4">{t('animal.factsHint')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {animal.facts.map((fact, index) => (
              <ScratchFact key={index} fact={fact} index={index} />
            ))}
          </div>
        </section>
      )}

      {/* Related animals */}
      {related && related.length > 0 && (
        <section>
          <h2 className="text-2xl font-bold mb-4">{t('animal.related')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {related.map((item) => (
              <Link key={item.id} to={`/animal/${item.slug}`}>
                <Card image={item.image_url} imageAlt={item.name} hover>
                  <h3 className="font-semibold truncate">{item.name}</h3>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
